import { useQueryClient } from "@tanstack/react-query";
import { createContext, SyntheticEvent, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { jaktdataApi } from "~/src/appGlobals";
import AlertWrapper from "~/src/components/Alerts/AlertWrapper";
import { DeleteIcon, SaveIcon } from "~/src/components/Icons/Icons";
import AppDataContext from "~/src/contexts/AppDataContext/DataProvider";
import { FormDataContext } from "~/src/contexts/FormDataContext/FormDataContext";
import { StorageContext } from "~/src/contexts/StorageContext/StorageProvider";
import useJaktdataHelper from "~/src/contexts/StorageContext/useJaktdataHelper";
import { isApiError } from "~/src/helpers/isApiErrorHelper";
import {
  getOmradeForOmradekode,
  getOmradeForSingleFavoritt,
  getOmradeKeyForArt,
} from "~/src/helpers/omradeHelper";
import { useClientState } from "~/src/hooks/useClientState";
import { CreateIndividRequest, Individ, NO_CONNECTION_STATUS_CODE } from "../../../api/types";
import AlertDefault, { AlertVariant } from "../../../components/Alerts/AlertDefault";
import ButtonDefault, {
  ButtonLayout,
  ButtonVariant,
} from "../../../components/Button/ButtonDefault";
import Card from "../../../components/Card/Card";
import { ComboboxOption } from "../../../components/Form/Combobox/Combobox";
import UIContext from "../../../contexts/UIContext/UIProvider";
import { toErrorRecords } from "../../../helpers/formErrors";
import { ArtType } from "../../../helpers/types";
import { useConfirmationDialog } from "../../../hooks/useConfirmationDialog";
import useFormData from "../../../hooks/useFormData";
import { useStateful } from "../../../hooks/useStateful";
import { postIndividQuery, putIndividQuery } from "../../../react-query/queries";
import { isMissingOvervakData } from "../../Oversikt/helpers";
import { handleOfflineIndividCreateOrUpdate } from "../Fellingsinfo/helpers";
import { parseErrors } from "../helpers";
import DatoOgJaktomrade from "../Sections/DatoOgJaktomrade";
import KvitteringsKopi from "../Sections/EpostBekreftelse";
import MerkelappOgCwd from "../Sections/MerkelappOgCwd";
import OmSkuttDyr from "../Sections/OmSkuttDyr";
import StedfestFelling from "../Sections/StedfestFelling";
import { IndividData, useIndividSchema } from "../schemas/schema";

type IndividFormProps = {
  initialInfo: IndividData;
  mode: "create" | "update";
  jaktdagGodkjent?: boolean;
  errors?: Partial<Record<keyof Individ, string>>;
  className?: string;
};

export const IndividFormDataContext = createContext<FormDataContext<IndividData> | undefined>(
  undefined,
);

const IndividForm = ({
  initialInfo,
  mode,
  jaktdagGodkjent,
  errors,
  className,
}: IndividFormProps) => {
  const scrollTopRef = useRef<HTMLDivElement>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof IndividData, string>>>(
    errors || {},
  );
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { schema } = useIndividSchema();
  const { showSnackbar } = useContext(UIContext);
  const appData = useContext(AppDataContext);
  const location = useLocation();
  const navigate = useNavigate();
  const storage = useContext(StorageContext);
  const { refetchJaktdataForJaktomrade } = useJaktdataHelper();
  const clientState = useClientState();
  const isOnline = clientState === "Online";
  const isSubmitting = useStateful(false);

  const favorittomrade = getOmradeForSingleFavoritt(appData, initialInfo?.Art);
  if (favorittomrade) {
    initialInfo[getOmradeKeyForArt(initialInfo.Art)] = favorittomrade.Områdekode;
  }
  const [formData, setFormData, removeFormData] = useFormData<IndividData>(initialInfo);

  const selectedJaktomradeNavn = getOmradeForOmradekode(
    formData?.Art,
    formData?.Villreinvald || formData?.Vald || formData?.Jaktfelt || "",
    appData,
  )?.Navn;

  const initialSelected = {
    id: formData?.Villreinvald || formData?.Vald || formData?.Jaktfelt || "",
    label: selectedJaktomradeNavn || "",
    data: {},
  } as ComboboxOption;
  const selectedJaktomrade = useStateful<ComboboxOption>(initialSelected);

  const navigateToOversiktSkutteDyr = () => {
    navigate("/oversikt", {
      state: { valgtOmradekode: selectedJaktomrade.value.id, activeTab: 1 },
    });
  };

  useEffect(() => {
    location.state = { routeProps: { valgtOmradekode: selectedJaktomrade.value.id } };
  }, [selectedJaktomrade.value]);

  async function handleOffline(_formData: IndividData) {
    handleOfflineIndividCreateOrUpdate({ mode, formData: _formData, storage });

    if (!isOnline) {
      showSnackbar({
        message: t("sync.noNetworkWillSyncLater"),
        severity: AlertVariant.Info,
      });
    }
    if (
      (initialInfo.Art == ArtType.Hjort || initialInfo.Art == ArtType.Elg) &&
      (initialInfo.Kjønn !== formData.Kjønn ||
        initialInfo.Alder !== formData.Alder ||
        initialInfo.Terreng !== formData.Terreng)
    ) {
      endreJaktdagDialog.show();
      isSubmitting.set(false);
    } else {
      removeFormData();
      await refetchJaktdataForJaktomrade(selectedJaktomrade.value.id).then(() => {
        navigateToOversiktSkutteDyr();
        isSubmitting.set(false);
      });
    }
  }

  // Submit
  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    isSubmitting.set(true);
    const _formData = { ...formData };

    _formData.Jegernummer = appData.jegeropplysninger.data?.Jegernummer;
    _formData.RegistrertAv = appData.jegeropplysninger.data?.Navn;
    _formData.RegistrertDato = new Date().toISOString();

    if (!_formData.Id) {
      _formData.Id = uuid();
    }

    const result = schema.safeParse(_formData);
    if (result.success) {
      setFormErrors({});
    } else {
      const fieldErrors: Partial<Record<keyof IndividData, string>> = {};
      result.error.issues.forEach((error) => {
        fieldErrors[error.path[0] as keyof IndividData] = error.message;
      });
      setFormErrors(fieldErrors);
      scrollTopRef.current?.scrollIntoView({ behavior: "smooth" });
      isSubmitting.set(false);
    }
    if (!result.success) return;

    const _isOnline = (await jaktdataApi.getClientState()) === "Online";
    if (!_isOnline) {
      handleOffline(_formData);
      return;
    }

    if (mode === "create") {
      postIndividQuery(_formData as CreateIndividRequest)
        .then(async () => {
          showSnackbar({
            message: t("pages.registrering.errors.registertSuksess"),
            severity: AlertVariant.Success,
          });
          removeFormData();
          const i = await jaktdataApi.getIndivid(_formData.Id);
          queryClient.setQueryData(["individId-" + i.Id, { individId: i.Id }], i);
          await refetchJaktdataForJaktomrade(selectedJaktomrade.value.id);
          navigateToOversiktSkutteDyr();
          isSubmitting.set(false);
        })
        .catch((error) => {
          const validationErrors = toErrorRecords<IndividData>(error);

          if (validationErrors && Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);

            scrollTopRef.current?.scrollIntoView({
              behavior: "smooth",
            });
          } else if (
            isApiError(error) ||
            error.axiosError?.code === "ECONNABORTED" ||
            error.statusCode === NO_CONNECTION_STATUS_CODE
          ) {
            handleOffline(_formData);
          }
          const formattedErrors = parseErrors(error.message);
          showSnackbar({
            message: formattedErrors.length
              ? formattedErrors
              : t("pages.registrering.errors.noeGikkGalt"),
            severity: AlertVariant.Error,
          });
          isSubmitting.set(false);
        });
    } else if (mode === "update") {
      putIndividQuery(_formData as CreateIndividRequest)
        .then(async () => {
          if (_formData.JaktdagId) {
            const j = await jaktdataApi.getJaktdag(_formData.JaktdagId);
            queryClient.setQueryData(["jaktdag", { jaktdagId: _formData.JaktdagId }], j);
          }

          if (
            (initialInfo.Art == ArtType.Hjort || initialInfo.Art == ArtType.Elg) &&
            (initialInfo.Kjønn !== formData.Kjønn ||
              initialInfo.Alder !== formData.Alder ||
              initialInfo.Terreng !== formData.Terreng)
          ) {
            showSnackbar({
              message: t("pages.registrering.errors.oppdatertSuksess"),
              severity: AlertVariant.Success,
            });
            endreJaktdagDialog.show();
            } else {
              const i = await jaktdataApi.getIndivid(_formData.Id);
              await queryClient.invalidateQueries({ queryKey: ["individ", _formData.Id] });

              removeFormData();

              if (_formData.JaktdagId) {
                await queryClient.invalidateQueries({
                  queryKey: ["jaktdag", { jaktdagId: _formData.JaktdagId }],
                });
              }
              showSnackbar({
                message: t("pages.registrering.errors.oppdatertSuksess"),
                severity: AlertVariant.Success,
              });
              queryClient.setQueryData(["individId-" + i.Id, { individId: i.Id }], i);
              await refetchJaktdataForJaktomrade(selectedJaktomrade.value.id);
              navigateToOversiktSkutteDyr();
              isSubmitting.set(false);
            }
        })
        .catch((error) => {
          if (error.axiosError?.code === "ECONNABORTED") {
            handleOffline(_formData);
            isSubmitting.set(false);
            return;
          }
          setFormErrors(toErrorRecords<IndividData>(error));

          scrollTopRef.current?.scrollIntoView({
            behavior: "smooth",
          });

          const formattedErrors = parseErrors(error.message);
          showSnackbar({
            message: formattedErrors.length
              ? formattedErrors
              : t("pages.registrering.errors.noeGikkGalt"),
            severity: AlertVariant.Error,
          });
          isSubmitting.set(false);
        });
    }
  };

  const endreJaktdagDialog = useConfirmationDialog({
    title: t("pages.registrering.dialogs.endreJaktdag.title"),
    message: t("pages.registrering.dialogs.endreJaktdag.message"),
    confirmButtonText: t("pages.registrering.dialogs.endreJaktdag.confirmButtonText"),
    buttonLayout: ButtonLayout.Vertical,
    onConfirm: () => {
      removeFormData();
      isSubmitting.set(false);
      refetchJaktdataForJaktomrade(selectedJaktomrade.value.id);
      navigate(`/oversikt/jaktdagdetaljer/${formData.JaktdagId}`);
    },
    onCancel: () => {
      removeFormData();
      isSubmitting.set(false);
      refetchJaktdataForJaktomrade(selectedJaktomrade.value.id);
      navigateToOversiktSkutteDyr();
    },
  });

  return (
    <IndividFormDataContext.Provider value={{ formData, setFormData, formErrors, setFormErrors }}>
      <div className={`grow ${className}`}>
        <div ref={scrollTopRef} />
        <form className="min-h-full relative flex flex-col justify-between">
          <Card color="transparent" shadow={false} className="h-full p-4 pt-0 ">
            <AlertWrapper>
              {isMissingOvervakData(formData) && (
                <AlertDefault variant={AlertVariant.Warning}>
                  {t("pages.fellingsinfo.overvakningsomradeWarning")}
                </AlertDefault>
              )}

              {formErrors.Id && (
                <AlertDefault variant={AlertVariant.Error}>{formErrors.Id}</AlertDefault>
              )}
            </AlertWrapper>

            {[ArtType.Villrein, ArtType.Rådyr].includes(formData.Art) && (
              <DatoOgJaktomrade selectedState={selectedJaktomrade} />
            )}
            <OmSkuttDyr jaktdagGodkjent={jaktdagGodkjent} />
            <MerkelappOgCwd />
            <StedfestFelling />
            <KvitteringsKopi />
          </Card>
          <div className="bg-ldir-blue-900 bottom-0 w-full p-6">
            <ButtonDefault
              className="focus:opacity-100"
              id="submit-form-registrer-individ"
              label={t("actions.save")}
              variant={ButtonVariant.PrimaryDarkBg}
              fullWidth
              type="button"
              onClick={(e) => handleSubmit(e)}
              disabled={isSubmitting.value}
              icon={<SaveIcon />}
            />
          </div>
        </form>
        {endreJaktdagDialog.element}
      </div>
    </IndividFormDataContext.Provider>
  );
};

export default IndividForm;
